class ArticleProductFeaturedList extends HTMLElement {
  constructor() {
    super();
    this.slider = null;
    this.inactivityTimer = null;
    this.isHovering = false;
  }

  connectedCallback() {
    const parent = this.querySelector('.js-tns-product-featured-list');
    const controls = this.querySelector('.parent-controls');

    if (!parent) return;

    const autoplay = this.dataset.autoplay === 'true';
    const autoplayTimeout = parseInt(this.dataset.autoplaySpeed, 10) || 3000;

    try {
      this.slider = tns({
        container: parent,
        items: 1,
        loop: true,
        rewind: false,
        controls: false,
        nav: false,
        mouseDrag: true,
        autoplay: autoplay,
        autoplayTimeout: autoplayTimeout,
        autoplayButtonOutput: false,
        gutter: 16,
        speed: 500
      });

      if (controls) {
        const prevBtn = controls.querySelector('.prev');
        const nextBtn = controls.querySelector('.next');

        if (prevBtn) {
          prevBtn.addEventListener('click', (event) => {
            event.preventDefault();
            this.slider.goTo('prev');
          });
        }

        if (nextBtn) {
          nextBtn.addEventListener('click', (event) => {
            event.preventDefault();
            this.slider.goTo('next');
          });
        }
      }

      if (this.slider && autoplay) {
        const scheduleResume = () => {
          clearTimeout(this.inactivityTimer);
          this.inactivityTimer = setTimeout(() => {
            if (!this.isHovering && this.slider) {
              this.slider.play();
            }
          }, autoplayTimeout);
        };

        this.addEventListener('mouseenter', () => {
          this.isHovering = true;
          clearTimeout(this.inactivityTimer);
          if (this.slider) this.slider.pause();
        });

        this.addEventListener('mouseleave', () => {
          this.isHovering = false;
          scheduleResume();
        });

        this.addEventListener('click', () => {
          if (this.slider) this.slider.pause();
          scheduleResume();
        });

        this.addEventListener(
          'touchstart',
          () => {
            if (this.slider) this.slider.pause();
            scheduleResume();
          },
          { passive: true }
        );
      }

      this.style.display = 'block';
    } catch (error) {
      console.warn('Error initializing featured products slider:', error);
    }
  }

  disconnectedCallback() {
    clearTimeout(this.inactivityTimer);

    if (this.slider && typeof this.slider.destroy === 'function') {
      try {
        this.slider.destroy();
      } catch (error) {
        console.warn('Error destroying featured products slider:', error);
      }
    }
  }
}

customElements.define('article-product-featured-list', ArticleProductFeaturedList);

class ArticleProductFeatured extends HTMLElement {
  static productState = new Map();

  constructor() {
    super();

    this.productVariants = [];
    this.inputId = null;
    this.priceContainer = null;
    this.button = null;
    this.form = null;
    this.mainImage = null;
    this.colorSliders = [];
    this.productKey = '';
    this.boundUpdateVariant = this.updateVariant.bind(this);
  }

  connectedCallback() {
    this.productVariants = this.parseVariants();
    this.inputId = this.querySelector('input[name="id"]');
    this.priceContainer = this.querySelector('.card-product__price .current-price');
    this.button = this.querySelector('.card-product__button');
    this.form = this.querySelector('form');
    this.mainImage = this.querySelector('.js-card-image');
    this.productKey = this.getProductKey();

    this.bindVariantEvents();
    this.initSharedState();
    this.syncFromSharedState();

    if (this.form) {
      this.form.addEventListener('submit', this.handleSubmit.bind(this));
    }

    this.style.display = 'flex';
    this.initColorSliders();
    this.preventNestedSliderConflicts();
  }

  disconnectedCallback() {
    this.colorSliders.forEach((instance) => {
      if (instance && typeof instance.destroy === 'function') {
        try {
          instance.destroy();
        } catch (error) {
          console.warn('Error destroying color slider:', error);
        }
      }
    });

    this.colorSliders = [];
  }

  parseVariants() {
    try {
      return JSON.parse(this.dataset.variants || '[]');
    } catch (error) {
      console.warn('Error parsing product variants:', error);
      return [];
    }
  }

  getProductKey() {
    if (this.id) return this.id;
    if (this.dataset.productId) return `card-${this.dataset.productId}`;

    const formIdInput = this.querySelector('form input[name="id"]');
    if (formIdInput) {
      const variantId = Number(formIdInput.value);
      const matched = this.productVariants.find((variant) => variant.id === variantId);
      if (matched && matched.product_id) {
        return `card-${matched.product_id}`;
      }
    }

    const titleLink = this.querySelector('.card-product__title a');
    if (titleLink?.getAttribute('href')) {
      return `product-${titleLink.getAttribute('href')}`;
    }

    return `product-${Math.random().toString(36).slice(2)}`;
  }

  initColorSliders() {
    const sliderContainers = this.querySelectorAll('.js-tns-color');

    sliderContainers.forEach((sliderContainer) => {
      const parent = sliderContainer.closest('.swatches-slider-wrapper');
      const controls = parent ? parent.querySelector('.custom-controls') : null;

      try {
        const instance = tns({
          container: sliderContainer,
          items: 3,
          loop: false,
          controlsContainer: controls || false,
          controls: !!controls,
          nav: false,
          mouseDrag: true,
          gutter: 8,
          speed: 300,
          responsive: {
            768: {
              items: 4
            }
          }
        });

        this.colorSliders.push(instance);
      } catch (error) {
        console.warn('Error initializing color slider:', error);
      }
    });
  }

  bindVariantEvents() {
    const radios = this.querySelectorAll('.variant-radio');

    radios.forEach((radio) => {
      radio.addEventListener('change', this.boundUpdateVariant);
    });

    this.querySelectorAll('.variant-item label').forEach((label) => {
      label.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();

        const variantItem = label.closest('.variant-item');
        if (!variantItem) return;

        const input = variantItem.querySelector('.variant-radio');
        if (!input) return;

        if (!input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      });
    });
  }

  preventNestedSliderConflicts() {
    this.querySelectorAll('.variant-item').forEach((item) => {
      item.addEventListener('mousedown', (event) => event.stopPropagation());
      item.addEventListener('touchstart', (event) => event.stopPropagation(), { passive: true });
      item.addEventListener('click', (event) => event.stopPropagation());
    });

    this.querySelectorAll('.tns-ovh').forEach((overflowContainer) => {
      overflowContainer.addEventListener('scroll', () => {
        overflowContainer.scrollLeft = 0;
      });
    });
  }

  initSharedState() {
    if (!this.productVariants.length || !this.productKey) return;

    if (ArticleProductFeatured.productState.has(this.productKey)) return;

    const currentVariantId = this.inputId ? Number(this.inputId.value) : null;
    const initialVariant =
      this.productVariants.find((variant) => variant.id === currentVariantId) ||
      this.getMatchedVariantFromDOM() ||
      this.productVariants.find((variant) => variant.available) ||
      this.productVariants[0];

    if (!initialVariant) return;

    ArticleProductFeatured.productState.set(this.productKey, {
      option1: initialVariant.option1 || null,
      option2: initialVariant.option2 || null,
      option3: initialVariant.option3 || null,
      variantId: initialVariant.id
    });
  }

  getSelectedOptionsFromDOM() {
    const wrappers = this.querySelectorAll('.variant-input-wrapper');

    return Array.from(wrappers).map((wrapper) => {
      const checked = wrapper.querySelector('.variant-radio:checked');
      return checked ? checked.value : null;
    });
  }

  getMatchedVariantFromDOM() {
    const selectedOptions = this.getSelectedOptionsFromDOM();

    return this.productVariants.find((variant) => {
      return selectedOptions.every((selectedValue, index) => {
        if (!selectedValue) return true;
        return variant[`option${index + 1}`] === selectedValue;
      });
    });
  }

  getMatchedVariantFromState(state) {
    if (!state) return null;

    return this.productVariants.find((variant) => {
      const option1Match = !state.option1 || variant.option1 === state.option1;
      const option2Match = !state.option2 || variant.option2 === state.option2;
      const option3Match = !state.option3 || variant.option3 === state.option3;

      return option1Match && option2Match && option3Match;
    });
  }

  updateVariant() {
    if (!this.productKey) return;

    const matchedVariant = this.getMatchedVariantFromDOM();
    if (!matchedVariant) return;

    ArticleProductFeatured.productState.set(this.productKey, {
      option1: matchedVariant.option1 || null,
      option2: matchedVariant.option2 || null,
      option3: matchedVariant.option3 || null,
      variantId: matchedVariant.id
    });

    this.syncAllInstances();
  }

  syncAllInstances() {
    const instances = document.querySelectorAll('article-product-featured');

    instances.forEach((instance) => {
      if (!(instance instanceof ArticleProductFeatured)) return;
      if (instance.productKey !== this.productKey) return;

      instance.syncFromSharedState();
    });
  }

  syncFromSharedState() {
    const state = ArticleProductFeatured.productState.get(this.productKey);
    if (!state) return;

    const matchedVariant =
      this.productVariants.find((variant) => variant.id === state.variantId) ||
      this.getMatchedVariantFromState(state);

    if (!matchedVariant) return;

    this.setRadioChecked(0, matchedVariant.option1);
    this.setRadioChecked(1, matchedVariant.option2);
    this.setRadioChecked(2, matchedVariant.option3);

    this.updateVariantLabels();
    this.updateFormVariantId(matchedVariant);
    this.updateMainImage(matchedVariant);
    this.updatePrice(matchedVariant);
    this.updateButtonState(matchedVariant);
  }

  setRadioChecked(optionIndex, value) {
    if (!value) return;

    const wrapper = this.querySelector(`.variant-input-wrapper[data-option-index="${optionIndex}"]`);
    if (!wrapper) return;

    wrapper.querySelectorAll('.variant-radio').forEach((radio) => {
      const selected = radio.value === value;
      radio.checked = selected;
      radio.closest('.variant-item')?.classList.toggle('is-selected', selected);
    });
  }

  updateVariantLabels() {
    const selectedRadios = this.querySelectorAll('.variant-radio:checked');

    selectedRadios.forEach((radio) => {
      if (!radio.dataset.labelCtn) return;

      const target = this.querySelector(`${radio.dataset.labelCtn} .option-selected`);
      if (target) {
        target.textContent = radio.value;
      }
    });
  }

  updateMainImage(variant) {
    if (!variant || !variant.featured_image || !this.mainImage) return;

    const rawSrc = variant.featured_image.src || '';
    if (!rawSrc) return;

    const normalizedSrc = rawSrc.startsWith('//') ? `https:${rawSrc}` : rawSrc;
    const optimizedSrc = normalizedSrc.replace(
      /(\.(jpg|jpeg|gif|png|bmp|tiff|webp))(\?.*)?$/i,
      '_300x$1$3'
    );

    this.mainImage.src = optimizedSrc;

    if (variant.featured_image.alt) {
      this.mainImage.alt = variant.featured_image.alt;
    }
  }

  updatePrice(variant) {
    if (!variant || !this.priceContainer) return;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      this.priceContainer.textContent = window.Shopify.formatMoney(
        variant.price,
        this.dataset.moneyFormat
      );
      return;
    }

    this.priceContainer.textContent = `$${(variant.price / 100).toFixed(2)}`;
  }

  updateButtonState(variant) {
    if (!this.button) return;

    const isAvailable = !!(variant && variant.available);
    this.button.disabled = !isAvailable;
    this.button.textContent = isAvailable ? 'Add to cart' : 'Sold out';
  }

  updateFormVariantId(variant) {
    if (!variant || !this.inputId) return;
    this.inputId.value = variant.id;
  }

  handleSubmit(event) {
    event.preventDefault();

    if (!this.form || !this.button) return;

    this.button.disabled = true;
    const originalText = this.button.textContent;
    this.button.textContent = 'Adding...';

    fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      body: new FormData(this.form),
      headers: {
        'X-Requested-With': 'XMLHttpRequest'
      }
    })
      .then((response) => response.json())
      .then((data) => {
        if (data && data.status && data.description) {
          throw new Error(data.description);
        }

        this.button.textContent = 'Added!';
        this.button.classList.add('is-success');

        document.dispatchEvent(
          new CustomEvent('cart:updated', {
            detail: { product: data }
          })
        );

        setTimeout(() => {
          this.button.textContent = originalText;
          this.button.disabled = false;
          this.button.classList.remove('is-success');
        }, 1000);
      })
      .catch((error) => {
        console.warn('Error adding product to cart:', error);
        this.button.textContent = 'Error';
        this.button.disabled = false;

        setTimeout(() => {
          this.button.textContent = originalText;
          this.syncFromSharedState();
        }, 1200);
      });
  }
}

customElements.define('article-product-featured', ArticleProductFeatured);